import clsx from 'clsx'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import FilterChip from 'LuxKit/Chips/FilterChip'
import PriceRowLuxPlusPriceWithCaption from 'LuxKit/PricePoints/LuxPlus/PriceRowLuxPlusPriceWithCaption'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import OfferBookButton from 'components/OfferPage/OfferBookButtons/OfferBookButton'
import useIsAvailableForPurchase from 'components/OfferPage/hooks/useIsAvailableForPurchase'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { VILLAS_REVIEWS_TOTAL_THRESHOLD } from 'constants/review'
import { VillasContentSections } from 'constants/villas'
import { HeaderHeightContext, HeaderInViewContext } from 'contexts/HeaderStateContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { centreElementInView } from 'lib/window/scrollUtils'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import {
  getCheapestSelectedPackageOptionWithDefault,
  getCurrentDateLabel,
  getFromDurationLabel,
} from 'selectors/offerPage/offerPageSelectors'
import zIndex from 'styles/tools/z-index'

import useVillasPrice from './useVillasPrice'

const Container = styled.div`
  position: fixed;
  top: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: 0;
  width: 100%;
  z-index: ${zIndex.stickyHeader};
  transform: translateY(-100%);
  opacity: 1;
  transition: transform 0.2s, opacity 0.2s, box-shadow 0.2s, top 0.2s;

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(12)};
  }

  &.visible {
    opacity: 1;
    transform: translateY(0);
    box-shadow: ${props => props.theme.shadow.bottom.medium};
  }
`

const PriceDetails = styled(Group)`
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
  padding-bottom: ${rem(8)};

  &.show {
    opacity: 1;
    pointer-events: auto;
  }
`

const VILLAS_CONTENT_SECTION_MESSAGES = defineMessages({
  [VillasContentSections.OVERVIEW]: {
    id: 'd_LcNCYPh0YbnsloqH_e',
    defaultMessage: 'Overview',
    description: 'Navigation tab label for villa overview section on the offer detail page',
  },
  [VillasContentSections.ROOMS_AND_AMENITIES]: {
    id: 'j2Ky1Z0t8YKrHmYoEOKv',
    defaultMessage: 'Rooms & amenities',
    description: 'Navigation tab label for villa rooms and amenities section',
  },
  [VillasContentSections.LOCATION]: {
    id: '-gnBgrSQ6TqNt4DAQ2nM',
    defaultMessage: 'Location',
    description: 'Navigation tab label for villa location section on the offer detail page',
  },
  [VillasContentSections.REVIEWS]: {
    id: 'OuzLAPbno2uKoC5LM9G5',
    defaultMessage: 'Reviews',
    description: 'Navigation tab label for villa reviews section on the offer detail page',
  },
  [VillasContentSections.THINGS_TO_KNOW]: {
    id: 'H0ZbhUKQyVoBsYum_QUS',
    defaultMessage: 'Things to know',
    description: 'Navigation tab label for villa things to know section',
  },
})

const showPriceDetailSections = new Set<VillasContentSections | undefined>([
  VillasContentSections.WHY_BOOK_WITH_US,
])

interface Props {
  onTabClick: (section: VillasContentSections) => void
  visibleSection?: VillasContentSections
  visible?: boolean
  packageView: App.OfferPackageOptionView
  dateLabel: string
  durationLabel: string
  rooms?: Array<App.Occupants>
  checkInDate?: string
  duration: number
  offer: App.VillaOffer
}

function VillasPageStickyHeader(props: Props) {
  const {
    onTabClick,
    offer,
    visibleSection,
    visible,
    duration,
    packageView,
    dateLabel,
    durationLabel,
    checkInDate,
    rooms,
  } = props

  const intl = useIntl()
  const ref = useRef<HTMLDivElement>(null)
  const tabButtonsRef = useRef<Record<string, HTMLButtonElement | null>>({})

  useAppHeaderObserver(ref, visible ? 'sticky' : 'hidden')

  const isMobile = useScreenSizeOnly('mobile')

  useEffect(() => {
    if (visibleSection && isMobile) {
      centreElementInView(tabButtonsRef.current[visibleSection])
    }
  }, [visibleSection, isMobile])

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const section = event.currentTarget.dataset.section as VillasContentSections
    onTabClick(section)
  }, [onTabClick])

  const [price, value, discount] = useVillasPrice(packageView)

  const availableForPurchase = useIsAvailableForPurchase()
  const dispatch = useOfferPageDispatch()
  const showPrices = packageView.capacityValid && packageView.hotelAvailable && availableForPurchase
  const canBook = !!(checkInDate && rooms && availableForPurchase)
  const onGuestsClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [dispatch])

  const onDatesClick = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [dispatch])

  const durationString = useMemo(() => checkInDate
    ? intl.formatMessage(
        {
          id: '4ueWQx9KKY4LRBwyk1t5',
          defaultMessage: '{count, plural, one {# night} other {# nights}}',
          description: 'Duration in nights for villa sticky header pricing',
        },
        { count: duration },
      )
    : durationLabel, [checkInDate, duration, durationLabel, intl])
  const showPriceDetails = showPriceDetailSections.has(visibleSection) || !visibleSection

  const headerHeights = useContext(HeaderHeightContext)
  const headerInViews = useContext(HeaderInViewContext)
  const heightOffset = headerInViews.inViews.siteStickyBanner ? headerHeights.heights.siteStickyBanner : 0

  const bookNowLabel = intl.formatMessage({
    id: 'TVksGe3or5YXr5xg-eDs',
    defaultMessage: 'Book now',
    description: 'Action button label to book villa offer in the sticky header',
  })
  const checkAvailabilityLabel = intl.formatMessage({
    id: 'zkdXmKsGWXipib_rf0oF',
    defaultMessage: 'Check availability',
    description: 'Button text to check villa availability in sticky header',
  })

  const headings = useMemo(() => {
    return Object.entries(VILLAS_CONTENT_SECTION_MESSAGES)
      .filter(([section]) => {
        if (section === VillasContentSections.REVIEWS) {
          return isOfferRatingDisplayable(offer.property.rating, VILLAS_REVIEWS_TOTAL_THRESHOLD)
        }
        return true
      })
      .map(([section, message]) => [section, intl.formatMessage(message)] as const)
  }, [offer, intl])

  return (
    <Container ref={ref} className={visible ? 'visible' : undefined} style={{ top: heightOffset }}>
      <LayoutContainer relative>
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
          <TabButtonsGroup>
            {headings.map(([section, label], index) => (
              <TabButton
                isActive={section === visibleSection}
                key={index}
                data-section={section}
                onClick={handleClick}
                size="medium"
                ref={(element) => { tabButtonsRef.current[section] = element }}
              >
                {label}
              </TabButton>
            ))}
          </TabButtonsGroup>
          <CSSBreakpoint min="desktop">
            <PriceDetails
              direction="horizontal"
              horizontalAlign="end"
              gap={16}
              verticalAlign="center"
              className={clsx({ show: showPriceDetails })}
            >
              <Group direction="vertical" horizontalAlign="end">
                {showPrices && <>
                  {canBook && <Group direction="horizontal" gap={4}>
                    <FilterChip size="small" onClick={onGuestsClick}>{formatOccupantsAsGuests(...rooms)}</FilterChip>
                    <FilterChip size="small" onClick={onDatesClick}>{dateLabel}</FilterChip>
                  </Group>}
                  <LuxPlusPriceGuard
                    showMemberPrice={price.member > 0}
                    fallbackPrice={<PriceRowPriceWithCaption
                      size="M"
                      caption={durationString}
                      price={price.hotel}
                      saleUnit="total"
                    />}
                  >
                    <PriceRowLuxPlusPriceWithCaption
                      size="M"
                      caption={durationString}
                      memberPrice={price.member}
                      saleUnit="total"
                    />
                  </LuxPlusPriceGuard>
                  {!!value && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={value}
                    discountPercentage={discount}
                  />}
                </>}
              </Group>
              <OfferBookButton packageView={packageView}>
                {canBook ? bookNowLabel : checkAvailabilityLabel}
              </OfferBookButton>
            </PriceDetails>
          </CSSBreakpoint>
        </Group>
      </LayoutContainer>
    </Container>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    dateLabel: getCurrentDateLabel(state),
    durationLabel: getFromDurationLabel(state, appState),
    checkInDate: state.checkInDate,
    rooms: state.rooms,
    duration: state.duration,
    offer: state.offer,
  }
}, VillasPageStickyHeader)
